import React from "react";
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram } from "lucide-react";
import Logo from "@/components/Logo";
import { site } from "@/lib/siteContent";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-navy-deep text-white border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          {/* Brand col */}
          <div className="md:col-span-5">
            <Logo variant="light" />
            <p className="mt-6 text-white/65 text-sm leading-relaxed max-w-sm">
              {site.brand.tagline}
            </p>
            <a
              href={site.brand.websiteUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-website-link"
              className="mt-6 inline-flex text-gold hover:text-gold-light text-sm font-semibold tracking-wide"
            >
              {site.brand.website} →
            </a>
          </div>

          {/* Nav col */}
          <div className="md:col-span-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold mb-5">
              Explore
            </div>
            <ul className="space-y-3">
              {site.nav.map((n) => (
                <li key={n.href}>
                  <a
                    href={n.href}
                    data-testid={`footer-link-${n.label.toLowerCase()}`}
                    className="text-sm text-white/75 hover:text-gold transition-colors"
                  >
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact col */}
          <div className="md:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold mb-5">
              Contact
            </div>
            <ul className="space-y-4 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-gold mt-0.5 shrink-0" />
                <div className="flex flex-col gap-1">
                  <a href={`mailto:${site.contact.emailGeneral}`} data-testid="footer-email-general" className="hover:text-gold transition-colors">
                    {site.contact.emailGeneral}
                    <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-white/45">General</span>
                  </a>
                  <a href={`mailto:${site.contact.emailCareers}`} data-testid="footer-email-careers" className="hover:text-gold transition-colors">
                    {site.contact.emailCareers}
                    <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-white/45">Careers / HR</span>
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-gold mt-0.5 shrink-0" />
                <a href={`tel:+27${site.contact.phone.replace(/\s|^0/g, "")}`} data-testid="footer-phone" className="hover:text-gold transition-colors">
                  {site.contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
                <span data-testid="footer-address">{site.contact.address}</span>
              </li>
            </ul>

            <div className="mt-6 flex items-center gap-3">
              {[Linkedin, Facebook, Instagram].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 border border-white/15 hover:border-gold hover:bg-gold/10 flex items-center justify-center text-white/70 hover:text-gold transition-all"
                  aria-label="Social link"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} {site.brand.longName}. All rights reserved.
          </div>
          <div className="text-xs text-white/40 uppercase tracking-[0.22em]">
            Building Businesses · Empowering People · Creating Legacy
          </div>
          <div data-testid="powered-by" className="text-[11px] text-white/40 tracking-wide">
            Powered by <span className="text-gold font-semibold">Elysiom</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
